import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Users, Save, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

interface SplitEntry {
  publisher_id: string;
  company_name: string;
  percentage: number;
  role_label: string;
}

interface PublisherResult {
  id: string;
  company_name: string;
}

interface CollaboratorSplitCardProps {
  workId: string;
}

export default function CollaboratorSplitCard({ workId }: CollaboratorSplitCardProps) {
  const { publisher } = useAuth();
  const [loadingContract, setLoadingContract] = useState(true);
  const [splits, setSplits] = useState<SplitEntry[]>([]);
  const [contractSince, setContractSince] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PublisherResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (workId && publisher?.id) fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workId, publisher?.id]);

  const fetchContract = async () => {
    if (!publisher?.id) return;
    setLoadingContract(true);
    try {
      const { data, error } = await supabase
        .from('revenue_contracts')
        .select('id, splits, effective_from')
        .eq('work_id', workId)
        .is('effective_to', null)
        .order('effective_from', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;

      const rawSplits = Array.isArray(data?.splits) ? (data!.splits as unknown as SplitEntry[]) : [];
      if (rawSplits.length > 0) {
        setContractSince(data!.effective_from);
        const ownerEntry = rawSplits.find(s => s.publisher_id === publisher.id);
        const collabs = rawSplits.filter(s => s.publisher_id !== publisher.id);
        const collabIds = collabs.map(c => c.publisher_id);
        const nameMap: Record<string, string> = {};
        if (collabIds.length > 0) {
          const { data: pubs } = await supabase
            .from('publishers')
            .select('id, company_name')
            .in('id', collabIds);
          (pubs || []).forEach(p => {
            nameMap[p.id] = p.company_name;
          });
        }
        setSplits([
          {
            publisher_id: publisher.id,
            company_name: publisher.company_name,
            percentage: ownerEntry?.percentage ?? 100,
            role_label: ownerEntry?.role_label ?? 'Studio',
          },
          ...collabs.map(c => ({
            publisher_id: c.publisher_id,
            company_name: nameMap[c.publisher_id] ?? 'Unknown studio',
            percentage: c.percentage,
            role_label: c.role_label ?? '',
          })),
        ]);
      } else {
        setSplits([
          {
            publisher_id: publisher.id,
            company_name: publisher.company_name,
            percentage: 100,
            role_label: 'Studio',
          },
        ]);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load revenue split');
    } finally {
      setLoadingContract(false);
    }
  };

  const collabTotal = splits.slice(1).reduce((sum, e) => sum + (e.percentage || 0), 0);
  const ownerPct = Math.max(0, 100 - collabTotal);
  const totalPct = splits.reduce((sum, e) => sum + (e.percentage || 0), 0);
  const isValid = Math.abs(totalPct - 100) < 0.01 && splits.every(e => e.percentage > 0);

  useEffect(() => {
    const handle = setTimeout(async () => {
      if (searchQuery.trim().length < 1) {
        setSearchResults([]);
        return;
      }
      setSearching(true);
      try {
        const { data, error } = await supabase.rpc('search_publishers_for_collab', {
          query: searchQuery.trim(),
        });
        if (error) throw error;
        const existingIds = new Set(splits.map(s => s.publisher_id));
        setSearchResults((data || []).filter(r => !existingIds.has(r.id)));
        setShowDropdown(true);
      } catch {
        // silent — search is best-effort
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [searchQuery, splits]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const addCollaborator = (pub: PublisherResult) => {
    setShowDropdown(false);
    setSearchQuery('');
    setSearchResults([]);
    const initialPct = Math.min(10, Math.max(0, 100 - collabTotal));
    setSplits(prev => [
      { ...prev[0], percentage: ownerPct - initialPct },
      ...prev.slice(1),
      { publisher_id: pub.id, company_name: pub.company_name, percentage: initialPct, role_label: '' },
    ]);
    setDirty(true);
  };

  const removeCollaborator = (index: number) => {
    setSplits(prev => {
      const next = prev.filter((_, i) => i !== index);
      const nc = next.slice(1).reduce((sum, e) => sum + e.percentage, 0);
      next[0] = { ...next[0], percentage: Math.max(0, 100 - nc) };
      return next;
    });
    setDirty(true);
  };

  const updateCollab = (index: number, field: 'percentage' | 'role_label', value: string | number) => {
    setSplits(prev => {
      const next = [...prev];
      if (field === 'percentage') {
        next[index] = { ...next[index], percentage: Number(value) };
        const nc = next.slice(1).reduce((sum, e) => sum + e.percentage, 0);
        next[0] = { ...next[0], percentage: Math.max(0, 100 - nc) };
      } else {
        next[index] = { ...next[index], role_label: value as string };
      }
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!isValid) {
      toast.error('Percentages must sum to 100 and each be positive');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.rpc('upsert_revenue_contract', {
        p_work_id: workId,
        p_splits: splits.map(({ publisher_id, percentage, role_label }) => ({
          publisher_id,
          percentage,
          role_label: role_label || 'Collaborator',
        })),
      });
      if (error) throw error;
      toast.success('Revenue split saved');
      setDirty(false);
      await fetchContract();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save revenue split');
    } finally {
      setSaving(false);
    }
  };

  if (loadingContract) {
    return (
      <Card className='partner-card'>
        <CardHeader className='pb-3'>
          <CardTitle className='text-base flex items-center gap-2'>
            <Users className='h-4 w-4' />
            Collaborators & Revenue Split
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
            Loading…
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='partner-card'>
      <CardHeader className='pb-3'>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle className='text-base flex items-center gap-2'>
              <Users className='h-4 w-4' />
              Collaborators & Revenue Split
            </CardTitle>
            <CardDescription className='mt-0.5'>
              Define how slice revenue is distributed. Splits applied at payout.
              {contractSince && (
                <span className='ml-2 text-xs opacity-60'>
                  Contract since {new Date(contractSince).toLocaleDateString()}
                </span>
              )}
            </CardDescription>
          </div>
          {dirty && (
            <Button size='sm' onClick={handleSave} disabled={saving || !isValid}>
              {saving ? (
                <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
              ) : (
                <Save className='h-3.5 w-3.5 mr-1.5' />
              )}
              Save Split
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='space-y-2'>
          {splits.map((entry, i) => {
            const isOwner = i === 0;
            return (
              <div
                key={entry.publisher_id}
                className={`flex items-center gap-3 rounded-lg p-3 text-sm ${isOwner ? 'bg-muted/40' : 'bg-muted/20'}`}
              >
                <div className='flex-1 min-w-0'>
                  <p className='font-medium truncate'>{entry.company_name}</p>
                  {isOwner ? (
                    <p className='text-xs text-muted-foreground'>Owner · remainder auto-calculated</p>
                  ) : (
                    <Input
                      value={entry.role_label}
                      onChange={e => updateCollab(i, 'role_label', e.target.value)}
                      placeholder='Role (e.g. Co-Director)'
                      className='mt-1 h-7 text-xs border-border/50'
                    />
                  )}
                </div>
                <div className='flex items-center gap-1 shrink-0'>
                  {isOwner ? (
                    <Badge variant='outline' className='font-mono text-sm min-w-[4rem] justify-center'>
                      {ownerPct.toFixed(1)}%
                    </Badge>
                  ) : (
                    <>
                      <Input
                        type='number'
                        min={0.1}
                        max={99.9}
                        step={0.1}
                        value={entry.percentage}
                        onChange={e => updateCollab(i, 'percentage', e.target.value)}
                        className='w-20 h-7 text-xs text-right font-mono'
                      />
                      <span className='text-xs text-muted-foreground'>%</span>
                      <Button
                        size='icon'
                        variant='ghost'
                        className='h-7 w-7 text-muted-foreground hover:text-destructive'
                        onClick={() => removeCollaborator(i)}
                      >
                        <Trash2 className='h-3.5 w-3.5' />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className='flex items-center justify-between text-xs'>
          <span className='text-muted-foreground'>Total</span>
          <span className={isValid ? 'text-green-400 font-mono' : 'text-destructive font-mono'}>
            {totalPct.toFixed(1)}% / 100%
          </span>
        </div>

        <div ref={searchRef} className='relative'>
          <Label className='text-xs text-muted-foreground mb-1.5 block'>Add collaborator</Label>
          <div className='relative'>
            <Search className='absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground' />
            <Input
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => searchQuery && setShowDropdown(true)}
              placeholder='Search studio name…'
              className='pl-8 h-8 text-sm'
            />
            {searching && (
              <Loader2 className='absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground' />
            )}
          </div>
          {showDropdown && searchResults.length > 0 && (
            <div className='absolute z-50 top-full mt-1 w-full rounded-lg border border-border bg-popover shadow-lg overflow-hidden'>
              {searchResults.map(pub => (
                <button
                  key={pub.id}
                  type='button'
                  className='w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted transition-colors'
                  onClick={() => addCollaborator(pub)}
                >
                  <Plus className='h-3.5 w-3.5 text-muted-foreground shrink-0' />
                  <span className='truncate'>{pub.company_name}</span>
                </button>
              ))}
            </div>
          )}
          {showDropdown && !searching && searchQuery.trim().length >= 1 && searchResults.length === 0 && (
            <div className='absolute z-50 top-full mt-1 w-full rounded-lg border border-border bg-popover p-3 text-xs text-muted-foreground shadow-lg'>
              No publishers found matching “{searchQuery}”
            </div>
          )}
        </div>

        {dirty && (
          <div className='flex items-center justify-between pt-2 border-t border-border/50'>
            <p className='text-xs text-muted-foreground'>Unsaved · splits applied at payout, not in real time.</p>
            <Button size='sm' onClick={handleSave} disabled={saving || !isValid}>
              {saving ? (
                <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
              ) : (
                <Save className='h-3.5 w-3.5 mr-1.5' />
              )}
              Save
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
